import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NameValueDto } from '@ig-core/interfaces/name-value-dto';
import { ReferenceCodeService } from '@app/admin/reference-code/reference-code.service';
import { ApplicationService } from '@app/applications/application.service';
import { LoanApplication, ApplicationAccessSpecifier } from '@app/applications/applications.model';
import { ReferenceCode } from '@app/admin/reference-code/reference-code.model';
import { ValidationService } from '../../../application-validators/validation.service'
import { MatSnackBar } from '@angular/material';
import { ConfirmationDialogService } from '@app/utils/confirmation-dialog/confirmation-dialog.service';
import { FileUploadService } from '@app/utils/file-upload/file-upload.service';
import { FolderFilesComposite } from '@app/applications/application-details/loanInformation/loan-folder-files/loan-folder-files.model';
import * as fileSaver from 'file-saver';
import { ImagePreviewDialogService } from '@app/utils/image-preview-dialog/image-preview-dialog.service';
import { IgFileService } from '@ig-core/form/igFile.service';
import { Applicant, ColletralDetails } from '../../collateral/collateralDetails/collateral-details.model';
import { ControlValidators } from '@app/applications/application-validators/control-validators.directive';
@Component({
  selector: 'eng-asset-info',
  templateUrl: 'asset-info.template.html',
  styleUrls: ['../../application-details.styles.scss', './asset-info.scss']
})
export class AssetInfoComponent implements OnInit {

  @Input()
  application: LoanApplication;
  @Input()
  menuCode: string;

  assetAssessmentCompositeDTO: any = {};
  assetDetailsForm: FormGroup;
  collateralTypes: ReferenceCode[] = [];
  collateralUses: NameValueDto[];
  livingStandards: NameValueDto[];

  isFormDisabled: boolean;
  allowAccess: boolean;
  menuItemAllowAccess: boolean;

  allFilesInFolder: FolderFilesComposite;

  applicationAccessSpecifiers: ApplicationAccessSpecifier[];
  menuItemAccessSpecifier: ApplicationAccessSpecifier;
  applicants: Applicant[];
  collateralApplicantsList: NameValueDto[] = [];
  collateralFiles: FolderFilesComposite;
  residenceFiles: FolderFilesComposite;
  ownershipTypes: NameValueDto[];
  //default value
  ltvLimit: number = 50;
  roofTypes: NameValueDto[];
  constructor(private formBuilder: FormBuilder,
    private referenceCodeService: ReferenceCodeService,
    private applicationService: ApplicationService,
    private _snackbar: MatSnackBar,
    private fileUploadService: FileUploadService,
    private imagePreviewDialogService: ImagePreviewDialogService,
    private confirmationDialogService: ConfirmationDialogService,
    private validationService: ValidationService,
    private fileService: IgFileService) {
    this.assetDetailsForm = this.formBuilder.group({
      collateralType: [''],
      collateralUse: [''],
      address: [''],
      ownedBy: [''],
      occupiedBy: [''],
      buildingAge: [''],
      distanceFromBranch: [''],
      buildingAreaSqft: [''],
      landAreaSqft: [''],
      buildingValuePerSqFt: [''],
      landValuePerSqFt: [''],
      fairMarketValueLand: [''],
      fairMarketValueBuilding: [''],
      totalPropertyValue: [''],
      maxLtv: [{ disabled: true }],

      familyBackground: [''],
      livingStandard: [''],
      otherEarningMembers: [''],
      landmarkConfirmation: [''],
      residenceDistanceFromBranch: [''],
      residenceOwnershipType: [''],
      ownerName: [''],
      ownerPhoneNumber: [''],
      rentPaidAmount: [''],
      addrAdditionalInfo: [''],
      constructionType: [''],
      residenceBuildingArea: [''],
      assetsSeen: [''],
      residenceVerificationComments: [''],

    });
    this.allowAccess = this.applicationService.allowAccess;
  }

  ngOnInit() {
    this.isFormDisabled = true;
    this.getRefCodes();
    this.fetchApplicants();
    this.fetchCustomerAssessment();
    this.getMenuItemAccess();
  }

  getRefCodes() {
    //it is possible that we may restrict that -prop must be present 
    let collateralSet = this.application.collateralSet
    this.referenceCodeService.getRefCodesForClassifier(collateralSet).subscribe((response: any) => {
      this.collateralTypes = response;
      this.applyValidatorsToAssetDetails();
    });

    this.referenceCodeService.getShortRefCodes('collateral_usedfor').subscribe((response: any) => {
      this.collateralUses = response.collateral_usedfor;
    });

    this.referenceCodeService.getShortRefCodes('living_standard').subscribe((response: any) => {
      this.livingStandards = response.living_standard;
    });

    this.referenceCodeService.getShortRefCodes('premise_ownership').subscribe((response: any) => {
      this.ownershipTypes = response.premise_ownership;
    });

    this.referenceCodeService.getShortRefCodes('residence_roof_type').subscribe((response: any) => {
      this.roofTypes = response.residence_roof_type;
    });

    //default ltvLimit value is 50, if field1 has value and if it is a number then we will set that value.
    this.referenceCodeService.getRefCodesForClassifier('assessment_settings').subscribe((response: any) => {
      const ltvMaxObject = response.find(e => e.code === 'LTV_MAX_LIMIT');

      if (ltvMaxObject && ltvMaxObject.field1 && ltvMaxObject.field1.toString().trim()) {
        let value = Number(ltvMaxObject.field1);
        this.ltvLimit = isNaN(value) ? 50 : value;
      }
    });
  }

  fetchApplicants() {
    this.applicationService.getApplicants(this.application.uid).subscribe((response: any) => {
      if (response) {
        this.applicants = response;
        this.collateralApplicants();
      }
    })
  }

  collateralApplicants() {
    this.applicants.forEach((applicantsObject) => {
      let applicant = { code: applicantsObject.uid, name: applicantsObject.name + '  ' + '(' + applicantsObject.play + ')' }
      this.collateralApplicantsList.push(<NameValueDto>applicant);
    })
  }

  fetchCustomerAssessment() {
    this.applicationService.getAssetAssessment(this.application.uid).subscribe((response: any) => {
      if (response.body && response.body.assetAssessment) {
        this.assetAssessmentCompositeDTO = response.body;
        this.buildCollateralDetailsForm(response.body.assetAssessment);
      }
    });
  }

  buildCollateralDetailsForm(assetAssessment?: any) {
    if (!assetAssessment) {
      return;
    }

    const collateralData =
      assetAssessment &&
      assetAssessment.collateralVerification &&
      assetAssessment.collateralVerification.collateralVerificationAssessmentData;

    const residenceData =
      assetAssessment &&
      assetAssessment.residenceVerification &&
      assetAssessment.residenceVerification.residenceVerificationAssessmentData;


    this.assetDetailsForm.patchValue({
      /* ---------------- Collateral Verification ---------------- */
      collateralType: collateralData ? collateralData.typeOfCollateral : null,
      collateralUse: collateralData ? collateralData.collateralUserFor : null,
      address: collateralData ? collateralData.address : null,
      ownedBy: collateralData ? collateralData.ownedBy : null,
      occupiedBy: collateralData ? collateralData.occupiedBy : null,
      buildingAge:
        collateralData && collateralData.buildingAge !== undefined
          ? collateralData.buildingAge
          : null,
      distanceFromBranch:
        collateralData && collateralData.distanceFromBranch !== undefined
          ? collateralData.distanceFromBranch
          : null,
      buildingAreaSqft:
        collateralData && collateralData.buildingArea !== undefined
          ? collateralData.buildingArea
          : null,
      landAreaSqft:
        collateralData && collateralData.landArea !== undefined
          ? collateralData.landArea
          : null,
      buildingValuePerSqFt:
        collateralData && collateralData.buildingValuePerSqFt !== undefined
          ? collateralData.buildingValuePerSqFt
          : null,
      landValuePerSqFt:
        collateralData && collateralData.landValuePerSqFt !== undefined
          ? collateralData.landValuePerSqFt
          : null,
      fairMarketValueLand:
        collateralData && collateralData.fairMarketValueOfLand !== undefined
          ? collateralData.fairMarketValueOfLand
          : 0,
      fairMarketValueBuilding:
        collateralData && collateralData.fairMarketValueOfBuilding !== undefined
          ? collateralData.fairMarketValueOfBuilding
          : 0,
      totalPropertyValue:
        collateralData && collateralData.totalPropertyValue !== undefined
          ? collateralData.totalPropertyValue
          : 0,
      maxLtv: this.ltvLimit,

      /* ---------------- Residence Verification ---------------- */
      familyBackground: residenceData ? residenceData.familyBackground : null,
      livingStandard: residenceData ? residenceData.livingStandard : null,
      otherEarningMembers: residenceData ? residenceData.otherEarningMembers : null,
      landmarkConfirmation: residenceData ? residenceData.landmarkConfirmation : null,
      residenceDistanceFromBranch:
        residenceData && residenceData.distanceFromBranch !== undefined
          ? residenceData.distanceFromBranch
          : null,
      residenceOwnershipType: residenceData ? residenceData.ownershipType : null,
      ownerName: residenceData && residenceData.ownerName !== undefined
        ? residenceData.ownerName
        : null,
      ownerPhoneNumber: residenceData && residenceData.ownerPhoneNumber !== undefined
        ? residenceData.ownerPhoneNumber
        : null,
      rentPaidAmount: residenceData && residenceData.rentPaidAmount !== undefined
        ? residenceData.rentPaidAmount
        : null,
      addrAdditionalInfo:
        residenceData && residenceData.addrAdditionalInfo !== undefined
          ? residenceData.addrAdditionalInfo
          : null,
      constructionType: residenceData ? residenceData.constructionType : null,
      residenceBuildingArea:
        residenceData && residenceData.buildingArea !== undefined
          ? residenceData.buildingArea
          : null,
      assetsSeen: residenceData ? residenceData.assetsObserved : null,
      residenceVerificationComments: residenceData ? residenceData.remarks : null
    });
    this.assetDetailsForm.disable();
    this.getSelectedFolderFiles(assetAssessment.collateralVerification.fileFolderUid, 'COLLATERAL');

    this.getSelectedFolderFiles(assetAssessment.residenceVerification.fileFolderUid, 'RESIDENCE');
  }


  saveAssetDetails() {
    this.validationService.markFormGroupTouched(this.assetDetailsForm)
    if (this.isFormDisabled) {
      return;
    }

    if (!this.assetDetailsForm.valid) {
      this._snackbar.open("Please fill all mandatory fields", "Close", {
        duration: 4000,
      });
      return;
    }

    if (!this.assetAssessmentCompositeDTO) {
      this.assetAssessmentCompositeDTO = {};
    }

    if (!this.assetAssessmentCompositeDTO.assetAssessment) {
      this.assetAssessmentCompositeDTO.assetAssessment = {};
    }

    const assetAssessment = this.assetAssessmentCompositeDTO.assetAssessment;

    if (!assetAssessment.collateralVerification) {
      assetAssessment.collateralVerification = {};
    }

    if (!assetAssessment.residenceVerification) {
      assetAssessment.residenceVerification = {};
    }

    let collateralVerificationFormFields = this.assetDetailsForm.controls;

    const collateralVerification = assetAssessment.collateralVerification;

    if (!collateralVerification.uid) {
      collateralVerification.contextUid = this.application.uid;
      collateralVerification.contextType = 'application';
      collateralVerification.category = 'COLLATERAL_VERIFICATION';
    }

    if (!collateralVerification.collateralVerificationAssessmentData) {
      collateralVerification.collateralVerificationAssessmentData = {};
    }

    const collateralData = collateralVerification.collateralVerificationAssessmentData;
    collateralData.typeOfCollateral = collateralVerificationFormFields.collateralType.value;
    collateralData.collateralUserFor = collateralVerificationFormFields.collateralUse.value;
    collateralData.address = collateralVerificationFormFields.address.value;
    collateralData.ownedBy = collateralVerificationFormFields.ownedBy.value;
    collateralData.occupiedBy = collateralVerificationFormFields.occupiedBy.value;
    collateralData.buildingAge = collateralVerificationFormFields.buildingAge.value;
    collateralData.distanceFromBranch = collateralVerificationFormFields.distanceFromBranch.value;
    collateralData.buildingArea = collateralVerificationFormFields.buildingAreaSqft.value;
    collateralData.landArea = collateralVerificationFormFields.landAreaSqft.value;
    collateralData.buildingValuePerSqFt = collateralVerificationFormFields.buildingValuePerSqFt.value;
    collateralData.landValuePerSqFt = collateralVerificationFormFields.landValuePerSqFt.value;
    collateralData.fairMarketValueOfLand = collateralVerificationFormFields.fairMarketValueLand.value;
    collateralData.fairMarketValueOfBuilding = collateralVerificationFormFields.fairMarketValueBuilding.value;
    collateralData.totalPropertyValue = collateralVerificationFormFields.totalPropertyValue.value;
    collateralData.maxLtvApplicable = this.ltvLimit;

    const residenceVerification = assetAssessment.residenceVerification;

    if (!residenceVerification.uid) {
      residenceVerification.contextUid = this.application.uid;
      residenceVerification.contextType = 'application';
      residenceVerification.category = 'RESIDENCE_VERIFICATION';
    }

    if (!residenceVerification.residenceVerificationAssessmentData) {
      residenceVerification.residenceVerificationAssessmentData = {};
    }

    const residenceVerificationAssessmentData = residenceVerification.residenceVerificationAssessmentData;

    residenceVerificationAssessmentData.familyBackground = collateralVerificationFormFields.familyBackground.value;
    residenceVerificationAssessmentData.livingStandard = collateralVerificationFormFields.livingStandard.value;
    residenceVerificationAssessmentData.otherEarningMembers = collateralVerificationFormFields.otherEarningMembers.value;
    residenceVerificationAssessmentData.landmarkConfirmation = collateralVerificationFormFields.landmarkConfirmation.value;
    residenceVerificationAssessmentData.distanceFromBranch = collateralVerificationFormFields.residenceDistanceFromBranch.value;
    residenceVerificationAssessmentData.ownershipType = collateralVerificationFormFields.residenceOwnershipType.value;
    residenceVerificationAssessmentData.ownerName = collateralVerificationFormFields.ownerName.value;
    residenceVerificationAssessmentData.ownerPhoneNumber = collateralVerificationFormFields.ownerPhoneNumber.value;
    residenceVerificationAssessmentData.rentPaidAmount = collateralVerificationFormFields.rentPaidAmount.value;
    residenceVerificationAssessmentData.addrAdditionalInfo = collateralVerificationFormFields.addrAdditionalInfo.value;
    residenceVerificationAssessmentData.constructionType = collateralVerificationFormFields.constructionType.value;
    residenceVerificationAssessmentData.buildingArea = collateralVerificationFormFields.residenceBuildingArea.value;
    residenceVerificationAssessmentData.assetsObserved = collateralVerificationFormFields.assetsSeen.value;
    residenceVerificationAssessmentData.remarks = collateralVerificationFormFields.residenceVerificationComments.value;

    const payload = {
      assetAssessment: assetAssessment
    };

    this.applicationService.saveAssetAssessment(this.application.uid, payload).toPromise().then(
      (_success) => {
        this._snackbar.open("Collateral updated successfully", "Close", {
          duration: 2000,
        });
        this.isFormDisabled = true;
        this.fetchCustomerAssessment();
        this.assetDetailsForm.disable();
      }, (failure) => {
        let errormesg = []
        errormesg.push(failure)
        this.applicationService.displayErrorMessages(errormesg);
      }
    );
  }

  enableDisableForm() {
    this.assetDetailsForm.enable();
    this.assetDetailsForm.controls.maxLtv.disable();
    this.assetDetailsForm.controls.totalPropertyValue.disable();
    this.onOwnershipTypeChange(this.assetDetailsForm.controls.residenceOwnershipType.value);
    this.isFormDisabled = false;
  }

  cancelForm() {
    this.isFormDisabled = true;
    this.buildCollateralDetailsForm(this.assetAssessmentCompositeDTO.assetAssessment);
    this.assetDetailsForm.disable();
  }
  //this function will apply validators to form group
  applyValidatorsToAssetDetails() {
    this.validationService.applyValidationRules(this.assetDetailsForm, "AssetAssessmentDetails").then((controlValidators) => {
    }).catch(() => {
    })
  }

  //this function will get either this menu item  is editable or readonly 
  getMenuItemAccess() {
    this.applicationService.getApplicationAccessModifiers(this.application.uid)
      .subscribe((response) => {
        this.applicationAccessSpecifiers = response.body;
        this.menuItemAccessSpecifier = this.applicationAccessSpecifiers
          .find(accessSpecifier => accessSpecifier.category === this.menuCode);
        if (this.menuItemAccessSpecifier) {
          this.menuItemAllowAccess = this.menuItemAccessSpecifier.allowAccess;
        }
      });
  }

  getCollateralTypeByCode(code) {
    if (this.collateralTypes.length != 0) {
      return this.collateralTypes.filter(collateralType => collateralType.code === code)[0];
    }
  }

  //this function open up the image upload dailog box
  //if file upload should allow single and multiple file upload, we need to pass "uploadType":"multiple"
  openImageFileUploadDialog(type: 'COLLATERAL' | 'RESIDENCE') {
    const folderUid =
      type === 'COLLATERAL'
        ? this.assetAssessmentCompositeDTO.assetAssessment
          .collateralVerification.fileFolderUid
        : this.assetAssessmentCompositeDTO.assetAssessment
          .residenceVerification.fileFolderUid;

    const files =
      type === 'COLLATERAL'
        ? this.collateralFiles.fileInfo
        : this.residenceFiles.fileInfo;
    this.fileUploadService.open({ "uploadType": "multiple", "folderUid": folderUid, "applicationUid": this.application.uid, "files": files })
    this.fileUploadService.confirmed().subscribe(data => {
      this.getSelectedFolderFiles(data.folderUid, type)
    })
  }


  getSelectedFolderFiles(folderUid, fileType: 'COLLATERAL' | 'RESIDENCE') {
    this.applicationService.getAllFilesInApplicationFolder(this.application.uid, folderUid).subscribe((response: any) => {
      if (response) {
        this.allFilesInFolder = response.body;
        this.allFilesInFolder.fileInfo.forEach(element => {
          if (element.fileSize) {
            element["convertedFileSize"] = this.applicationService.bytesToSize(element.fileSize)
          }
        });
        if (fileType === 'COLLATERAL') {
          this.collateralFiles = this.allFilesInFolder;
        } else {
          this.residenceFiles = this.allFilesInFolder;
        }
      }
    });
  }

  //this function will call onclick of download icon in file's section
  downloadFile(file) {
    this.applicationService.downloadFileFromApplicationFolder(this.application.uid, file.folderUid, file.fileId).subscribe(image => {
      const blob = new Blob([image.data], { type: file.type });
      fileSaver.saveAs(blob, file.fileName);
      this._snackbar.open("File has been downloaded successfully", "Close", {
        duration: 5000,
      });
    })
  }

  //this function will trigger onclicking of image,will open up a mat-dailog
  openContentElement(fileType) {
    this.imagePreviewDialogService.open({ "images": fileType === 'COLLATERAL' ? this.collateralFiles : this.residenceFiles })
  }


  openfileDeleteConfirmation(file: any, fileType: 'COLLATERAL' | 'RESIDENCE') {
    this.confirmationDialogService.open({ "btnCancelText": "Cancel", "btnConfirmText": "Delete", "message": "Are you sure you want to delete this File?" })
    this.confirmationDialogService.confirmed().subscribe(data => {
      if (data) {
        this.deleteFile(file, fileType)
      }
    })

  }

  //this function will be called on click of delete icon on file list
  deleteFile(selectedFile, fileType) {
    this.applicationService.fileDelete(this.application.uid, selectedFile.uid).subscribe((response) => {
      if (response.body.status == "success") {
        this._snackbar.open("File deleted successfully", "Close", {
          duration: 2000,
        });
        if (fileType === 'COLLATERAL') {
          this.getSelectedFolderFiles(
            this.assetAssessmentCompositeDTO.assetAssessment
              .collateralVerification.fileFolderUid,
            'COLLATERAL'
          );
        } else {
          this.getSelectedFolderFiles(
            this.assetAssessmentCompositeDTO.assetAssessment
              .residenceVerification.fileFolderUid,
            'RESIDENCE'
          );
        }
      } else {
        let errormesg = []
        errormesg.push("File deleted failed")
        errormesg.push(response.body.message)
        this.applicationService.displayErrorMessages(errormesg);
      }
    }, (failure) => {
      let errormesg = []
      errormesg.push("File deleted failed")
      errormesg.push(failure)
      this.applicationService.displayErrorMessages(errormesg);
    });
  }

  viewPdf(file) {
    this.fileService.fileStreamById(file.fileId, "false").subscribe(res => {
      window.open(res.data, '_blank');
    })
  }

  calculatePropertyValue() {
    const buildingArea = Number(this.assetDetailsForm.controls.buildingAreaSqft.value) || 0;
    const buildingRate = Number(this.assetDetailsForm.controls.buildingValuePerSqFt.value) || 0;

    const landArea = Number(this.assetDetailsForm.controls.landAreaSqft.value) || 0;
    const landRate = Number(this.assetDetailsForm.controls.landValuePerSqFt.value) || 0;

    let fairMarketValueBuilding = buildingArea * buildingRate;
    let fairMarketValueLand = landArea * landRate;

    let totalPropertyValue = fairMarketValueBuilding + fairMarketValueLand;

    this.assetDetailsForm.patchValue({
      fairMarketValueBuilding: fairMarketValueBuilding,
      fairMarketValueLand: fairMarketValueLand,
      totalPropertyValue: totalPropertyValue
    }, { emitEvent: false });
  }

  onOwnershipTypeChange(value?: string, isDropdownChange: boolean = false) {
    const control = this.assetDetailsForm.controls.rentPaidAmount;
    // Clear the value when dropdown is changed by user
    if (isDropdownChange) {
      control.setValue(null);
      this.assetDetailsForm.controls.ownerName.setValue(null);
      this.assetDetailsForm.controls.ownerPhoneNumber.setValue(null);
    }
    if (value !== 'RENTD') {
      this.assetDetailsForm.controls['rentPaidAmount']
        .setValidators([ControlValidators.numberValidator("Rent paid should be a number")]);
    } else {
      this.assetDetailsForm.controls['rentPaidAmount']
        .setValidators([ControlValidators.requiredValidator("Rent paid amount is required"),
        ControlValidators.numberValidator("Rent paid should be a number")]);
    }
    control.updateValueAndValidity();
  }

  onOwnerSelected(selectedApplicantUid: any) {
    let selectedApplicant = this.applicants.find(applicant => applicant.uid === selectedApplicantUid);
    if (selectedApplicant) {
      this.assetDetailsForm.patchValue({
        ownerPhoneNumber: selectedApplicant.primaryPhone
      });
    }
  }

}